// "History and notes" — a field-diff audit trail + free-text notes, one shared component for
// documents (and, in principle, any other resource exposing the same {at,user,action,detail}
// GET .../activity + {text} POST .../notes contract). Ported from invoiceTest's
// historynotes.js (documents-parity sweep Wave C2, owner 2026-07-29: "there should be a history
// and notes on all documents, not just for customers — and also add history and notes to
// customers, similar to xero"). CAPTURED FROM THE REAL XERO (org "test", 2026-07-29): collapsible
// section headed "History and notes", chevron left, "Add note" button right; a table
// Date (desc) · User · Action · Detail, newest first; Detail is a SENTENCE about the document
// ("INV-0001 Total changed from 0.00 to 1419.10"), not a bare label.
//
// FOCUS TRAP (CLAUDE.md, validated): the "Add note" textarea + the search input must NEVER live
// inside a tina4 reactive slot — document_detail.js's body() is ONE flat top-level slot that
// re-runs on many unrelated signals (notice/busy/payOpen/sendOpen/…), and any ${()=>…} slot tina4
// re-runs is torn down and rebuilt from a FRESH DocumentFragment (confirmed against tina4js's own
// core.cjs.js: a function-value interpolation removes its previously-inserted nodes and inserts
// brand new ones on every dependency change) — an <input>/<textarea> recreated like that loses
// focus and any unsaved keystroke. So, exactly like the source: this is built IMPERATIVELY,
// entirely OUTSIDE tina4's reactivity (plain DOM, `wrap.querySelector`/`replaceChildren`, no
// signals for query/sortKey/sortDir/composing) — `refresh()` (full rebuild) only ever runs from a
// discrete click (toggle/add-note/cancel/save), never while the user is mid-keystroke;
// `paintBody()` (repaint ONLY the table body) is what a search keystroke triggers, leaving the
// search input untouched.
//
// MOUNTING CONTRACT: renderHistoryAndNotes(...) returns a raw, STABLE DOM Node — call it ONCE per
// screen instance (like attachmentsCard's factory) and interpolate the SAME node reference every
// time the caller's template re-renders (`${historyNotesNode}`, never `${historyNotesNode()}` and
// never wrapped in `${() => …}`). tina4's `html` tag inserts a plain Node value ONCE with no
// reactive effect of its own (no `isSignal`/`typeof === 'function'` branch matches a bare Node),
// and on a later render of the SAME interpolation slot it MOVES (not clones) that same node into
// the fresh tree — so its children/listeners/internal state survive the caller's re-renders.
// Calling the factory fresh on every render (the attachmentsCard-style "factory once, view
// function called plainly every render" pattern) would NOT be safe here, because it would rebuild
// a brand-new textarea/search input on every unrelated signal change.
//
// RESIDUAL FOCUS RISK, and the fix: even with the node reference held stable, the MOVE above is
// implemented as `parentNode.replaceChild` on the CALLER's fresh clone while this node is still a
// child of the CALLER's outgoing (still-connected, still-displayed) old tree — which means it is
// briefly detached from `document` mid-move (verified against tina4js's core.cjs.js: the marker
// swap runs while building the new, not-yet-inserted clone, and only the OUTER effect's later
// insertBefore reconnects it). A detach — even a same-tick one — drops focus off a focused
// descendant (a focused input's `document.activeElement` falls back to `<body>` the instant it's
// disconnected; reattaching it afterward does NOT restore focus by itself — reproduced directly
// against jsdom). So a focused search input / note textarea inside this component would silently
// lose focus on ANY unrelated repaint of document_detail.js's body() slot (e.g. a notice appearing
// after some other action) — the exact footgun this whole file exists to avoid. Real browsers fire
// a `blur` event at the moment of that disconnect (jsdom, verified, does not — it only updates
// `document.activeElement`), so the fix below can't rely on `blur`; instead it watches
// `document.body` with a MutationObserver (batched, DOM-structure-based, not event-based — behaves
// identically in jsdom and in real browsers) and refocuses the last-focused field of THIS
// component the moment it notices that field is still connected but activeElement fell back to
// `<body>` — never stealing focus when the user deliberately focused something else instead
// (activeElement then is that OTHER live element, not `<body>`, so the guard below leaves it alone).
import { html } from './t4.js';
import { icon } from './icons.js';
import { api } from './api.js';
import { t } from './i18n.js';
import './sleekdate.js';   // registers <sleek-date> — the follow-up field uses the app date component

const BAD_ACTIONS = new Set(['Voided', 'Cancelled', 'Send failed', 'Declined', 'Written off']);
const GOOD_ACTIONS = new Set(['Payment received', 'Approved', 'Received']);

const pad = n => String(n).padStart(2, '0');

/** "28/07/2026 10:45 am" — dd/mm/yyyy + 12-hour time, in the viewer's local timezone. Postgres's
 * own str(timestamptz) is space-separated ("2026-09-03 16:49:09.205123+00:00"), not the 'T'
 * separator Date.parse is guaranteed to accept, so that gets normalised first. Never throws —
 * falls back to the raw value if it can't be parsed.
 *
 * A date-ONLY value (no time — documents.activity.at is always a real timestamptz today, but this
 * stays defensive in case a caller ever passes a bare date) is deliberately NOT run through
 * `Date`+local-getters for its date portion: `new Date('2026-07-28')` parses as UTC MIDNIGHT, and
 * in any timezone behind UTC, `getDate()` on that instant reads back the PREVIOUS local day — a
 * real off-by-one for anyone west of Greenwich. A bare calendar day has no instant to convert
 * (there's no source timezone to convert FROM), so its digits are read directly off the string. */
export function formatStamp(at){
  const raw = String(at || '').trim();
  if(!raw) return '';
  const hasTime = /\d{2}:\d{2}/.test(raw);
  if(!hasTime){
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw);
    return m ? (m[3] + '/' + m[2] + '/' + m[1]) : raw;
  }
  const iso = raw.includes(' ') && !raw.includes('T') ? raw.replace(' ', 'T') : raw;
  const dt = new Date(iso);
  if(Number.isNaN(dt.getTime())) return raw;
  const date = pad(dt.getDate()) + '/' + pad(dt.getMonth() + 1) + '/' + dt.getFullYear();
  let hour = dt.getHours();
  const minute = pad(dt.getMinutes());
  const suffix = hour >= 12 ? 'pm' : 'am';
  hour = hour % 12 || 12;
  return date + ' ' + hour + ':' + minute + ' ' + suffix;
}

/**
 * @param {string} base    API root for this resource, e.g. '/companies/42/documents/7' — reads
 *                         GET `${base}/activity` ({data:[{at,user,action,detail}]}) and writes
 *                         POST `${base}/notes` ({text}).
 * @param {string} [title] card heading — default 'History and notes'.
 * @param {string} [intro] optional intro line shown only while expanded.
 * @param {boolean} [open] start expanded (Xero starts collapsed).
 * @returns {Node} a stable DOM node — see the MOUNTING CONTRACT note above.
 */
// `ctl` (optional, non-breaking): a plain object the component populates with `ctl.reload` — a
// function the CALLER can invoke to re-fetch the feed after it made a server-side activity change
// elsewhere on the page (e.g. document_detail's expected-payment-date save logs an activity row).
// `followUp` (optional): when true, the Add-note composer offers an OPTIONAL follow-up date.
// Setting a date posts the note as a reminder/task (is_task + remind_date); leaving it blank
// posts a plain note (owner Andre 2026-09-17: "Add note needs a follow up date … Follow is
// optional"). Off by default so document notes stay a plain textarea.
export function renderHistoryAndNotes({ base, title, intro = '', open = false, ctl, followUp = false }){
  const heading = title || t('hn.title', 'History and notes');
  const wrap = document.createElement('div');
  wrap.className = 'hn-card';
  let expanded = !!open;
  let composing = false;                        // the "Add note" textarea is showing

  let rows = [];                                 // decorated {at,stamp,user,action,detail,tone,seq}
  let loading = true;
  let loadErr = '';
  let seq = 0;                                   // out-of-order response guard (attachmentsCard-style)

  // Search + sort state — plain closure vars, NOT signals (see the module note above).
  let query = '';
  let sortKey = 'at';           // 'at' | 'user' | 'action' | 'detail'
  let sortDir = 'desc';         // Date defaults newest-first

  // Focus-restore safety net for the RESIDUAL risk described in the module note — remembers
  // whichever of our own inputs (search box / note textarea) was last focused, and puts focus back
  // on it if an outer repaint silently dropped it to <body> while that field is still connected.
  let lastFocused = null;
  let everMounted = false;                       // latch: only self-disconnect AFTER a real mount
  function trackFocus(el){ el.addEventListener('focus', () => { lastFocused = el; }); }
  const focusGuard = new MutationObserver(() => {
    // Self-teardown: this observer watches the whole document.body, and renderHistoryAndNotes runs
    // once PER screen visit (MOUNTING CONTRACT above) — so without this it would leak one permanent
    // global observer on every document/contact detail visit. Once our root has been mounted and is
    // then removed (navigating away swaps document_detail's tree, which fires this observer), tear
    // the observer down. The everMounted latch guards the pre-mount window: a body mutation firing
    // before the caller has inserted our node must NOT be mistaken for an unmount.
    if(wrap.isConnected){ everMounted = true; }
    else if(everMounted){ focusGuard.disconnect(); lastFocused = null; return; }
    else return;                                  // not mounted yet — nothing to guard, don't tear down
    if(!lastFocused || !lastFocused.isConnected) { lastFocused = null; return; }
    if(document.activeElement === lastFocused) return;             // already correct — nothing to do
    if(document.activeElement && document.activeElement !== document.body) return;  // focus moved elsewhere, deliberately — respect it
    const pos = typeof lastFocused.selectionStart === 'number' ? lastFocused.selectionStart : null;
    lastFocused.focus();
    if(pos != null){ try { lastFocused.setSelectionRange(pos, pos); } catch { /* not a text-ish input */ } }
  });
  focusGuard.observe(document.body, { childList: true, subtree: true });

  const COLS = [
    { key: 'at', label: t('hn.col_date', 'Date') },
    { key: 'user', label: t('hn.col_user', 'User') },
    { key: 'action', label: t('hn.col_action', 'Action') },
    { key: 'detail', label: t('hn.col_detail', 'Detail') },
  ];

  function decorate(apiRows){
    const n = apiRows.length;
    return apiRows.map((r, i) => ({
      at: r.at || '', stamp: formatStamp(r.at), user: r.user || '—',
      action: r.action || '—', detail: r.detail || '',
      tone: BAD_ACTIONS.has(r.action) ? 'bad' : GOOD_ACTIONS.has(r.action) ? 'ok' : '',
      seq: n - 1 - i,           // insertion-order tie-break (oldest=0) — the API arrives newest-first
    }));
  }

  async function load(){
    const mySeq = ++seq;
    loading = true; loadErr = '';
    try {
      const r = await api.get(base + '/activity');
      if(mySeq !== seq) return;               // a newer load already superseded this one
      rows = decorate(r.data || []);
    } catch(e){
      if(mySeq !== seq) return;
      loadErr = (e.data && e.data.error) || e.message || t('hn.load_failed', 'Could not load history and notes');
    }
    if(mySeq === seq) loading = false;
  }

  function viewRows(allRows){
    const q = query.trim().toLowerCase();
    const list = q
      ? allRows.filter(r => (r.stamp + ' ' + r.user + ' ' + r.action + ' ' + r.detail).toLowerCase().includes(q))
      : allRows.slice();
    const dir = sortDir === 'asc' ? 1 : -1;
    const keyVal = r => sortKey === 'at' ? r.at : String(r[sortKey] || '').toLowerCase();
    list.sort((a, b) => {
      const av = keyVal(a), bv = keyVal(b);
      if(av < bv) return -dir;
      if(av > bv) return dir;
      return (a.seq - b.seq) * dir;                    // tie-break by insertion order, same direction
    });
    return list;
  }

  // Repaint ONLY the table body — leaves the search input (and, if open, the note textarea)
  // untouched so either keeps focus + caret mid-type.
  function paintBody(){
    const host = wrap.querySelector('[data-body]');
    if(!host) return;
    if(loading){ host.replaceChildren(html`<div class="muted-block">${t('common.loading', 'Loading…')}</div>`); return; }
    if(loadErr){ host.replaceChildren(html`<div class="row-err">${loadErr}</div>`); return; }
    const badge = wrap.querySelector('.grid-count');
    if(!rows.length){
      if(badge) badge.remove();
      host.replaceChildren(html`<div class="muted-block">${t('hn.empty', 'Nothing yet — actions on this document will be listed here.')}</div>`);
      return;
    }
    const list = viewRows(rows);
    // Header count stays honest: total when unfiltered, "n of total" while a search narrows it.
    if(badge) badge.textContent = (query.trim() && list.length !== rows.length)
      ? list.length + ' of ' + rows.length : String(rows.length);

    const arrow = k => sortKey === k ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';
    const sortOn = k => {
      if(sortKey === k) sortDir = sortDir === 'asc' ? 'desc' : 'asc';
      else { sortKey = k; sortDir = k === 'at' ? 'desc' : 'asc'; }   // Date defaults desc, text asc
      paintBody();
    };
    host.replaceChildren(list.length
      ? html`<div class="tbl-wrap"><table class="jrn-table hn-table">
          <thead><tr>${COLS.map(c => html`<th class="srt" role="button" tabindex="0"
              aria-sort=${sortKey === c.key ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
              @click=${() => sortOn(c.key)}
              @keydown=${e => { if(e.key === 'Enter' || e.key === ' '){ e.preventDefault(); sortOn(c.key); } }}
            >${c.label}${arrow(c.key)}</th>`)}</tr></thead>
          <tbody>${list.map(r => html`<tr>
            <td class="mono hn-date">${r.stamp}</td>
            <td>${r.user}</td>
            <td><span class=${'hn-action' + (r.tone ? ' hn-' + r.tone : '')}>${r.action}</span></td>
            <td>${r.detail}</td></tr>`)}</tbody>
        </table></div>`
      : html`<div class="muted-block">${t('hn.no_match', 'No history or notes match')} “${query.trim()}”.</div>`);
  }

  function build(){
    const total = rows.length;
    const frag = html`
      <div class="hn-head">
        <span class="hn-toggle" role="button" tabindex="0" aria-expanded=${String(expanded)}>
          <span class="hn-chev">${expanded ? icon('chevron-down', {size: 12}) : icon('chevron-right', {size: 12})}</span><b>${heading}</b>
          ${total ? html`<span class="grid-count">${total}</span>` : null}
        </span>
        <button type="button" class="btn btn-sm" data-addnote>${t('hn.add_note', 'Add note')}</button>
      </div>
      <div data-compose></div>
      <div data-intro></div>
      <div data-toolbar></div>
      <div data-body></div>`;

    frag.querySelector('.hn-toggle').addEventListener('click', () => { expanded = !expanded; refresh(); });
    frag.querySelector('.hn-toggle').addEventListener('keydown', e => {
      if(e.key === 'Enter' || e.key === ' '){ e.preventDefault(); expanded = !expanded; refresh(); }
    });
    frag.querySelector('[data-addnote]').addEventListener('click', () => {
      composing = true; expanded = true; refresh();
      const ta = wrap.querySelector('[data-note-text]');
      if(ta) ta.focus();
    });

    if(composing){
      const box = html`
        <div class="hn-compose">
          <textarea class="form-control" data-note-text rows="2"
            placeholder="e.g. Client asked us to hold delivery until the 5th"></textarea>
          ${followUp ? html`<label class="hn-followup">
            <span>${t('hn.follow_up', 'Follow-up date')} <span class="muted">(${t('common.optional', 'optional')})</span></span>
            <sleek-date data-free="true" data-note-follow></sleek-date>
          </label>` : ''}
          <div class="form-actions">
            <button type="button" class="btn btn-primary btn-sm" data-note-save>${t('hn.save_note', 'Save note')}</button>
            <button type="button" class="btn btn-sm btn-outline" data-note-cancel>${t('common.cancel', 'Cancel')}</button>
            <span class="row-err" data-note-err></span>
          </div>
        </div>`;
      trackFocus(box.querySelector('[data-note-text]'));
      box.querySelector('[data-note-cancel]').addEventListener('click', () => { composing = false; refresh(); });
      const saveBtn = box.querySelector('[data-note-save]');
      saveBtn.addEventListener('click', async () => {
        const ta = wrap.querySelector('[data-note-text]');
        const errEl = wrap.querySelector('[data-note-err]');
        const text = (ta.value || '').trim();
        if(!text){ if(errEl) errEl.textContent = t('hn.type_note_first', 'Type the note first.'); return; }
        // A follow-up date is optional; when present the note posts as a reminder/task.
        const followEl = wrap.querySelector('[data-note-follow]');
        const remind = followEl && followEl.value ? followEl.value : null;
        saveBtn.disabled = true; saveBtn.textContent = t('common.saving', 'Saving…');
        if(errEl) errEl.textContent = '';
        try {
          await api.post(base + '/notes',
            remind ? { text, remind_date: remind, is_task: true } : { text });
          composing = false;
          await load();
          refresh();
        } catch(e){
          if(errEl) errEl.textContent = (e.data && e.data.error) || e.message || t('hn.note_failed', 'Could not save the note');
          saveBtn.disabled = false; saveBtn.textContent = t('hn.save_note', 'Save note');
        }
      });
      frag.querySelector('[data-compose]').replaceWith(box);
    } else {
      frag.querySelector('[data-compose]').remove();
    }

    if(!expanded){
      frag.querySelector('[data-intro]').remove();
      frag.querySelector('[data-toolbar]').remove();
      frag.querySelector('[data-body]').remove();
      return frag;
    }
    if(intro) frag.querySelector('[data-intro]').replaceWith(html`<div class="muted hn-intro">${intro}</div>`);
    else frag.querySelector('[data-intro]').remove();

    if(loading || loadErr || !total){
      frag.querySelector('[data-toolbar]').remove();
      return frag;                      // paintBody() (called by refresh()) fills [data-body]
    }

    // A search box (created ONCE) above the sortable table. Only the body repaints on input, so
    // the field keeps focus while you type. Sorting is per-column via the clickable headers below.
    const toolbar = html`<div class="list-toolbar hn-toolbar">
      <input class="form-control" type="search" aria-label=${t('hn.search_aria', 'Search history and notes')}
        placeholder="e.g. Sipho, Voided, 25/08/2026">
    </div>`;
    const searchEl = toolbar.querySelector('input');
    searchEl.value = query;
    searchEl.addEventListener('input', () => { query = searchEl.value; paintBody(); });
    trackFocus(searchEl);
    frag.querySelector('[data-toolbar]').replaceWith(toolbar);
    return frag;
  }

  function refresh(){ wrap.replaceChildren(build()); paintBody(); }

  refresh();                      // an immediate first paint (collapsed header, or a loading body)
  load().then(refresh);           // then the real fetch — repaints once it resolves (or errors)

  // Let the caller re-fetch after it logs an activity elsewhere on the page (non-breaking opt-in).
  if(ctl && typeof ctl === 'object') ctl.reload = async () => { await load(); refresh(); };

  return wrap;
}
